/**
 * Gerador do QR de coleta que o coletor lê (RF-010 a RF-012, RF-094, Anexo B.1).
 *
 * Existe porque a spec não diz quem imprime esse QR no cliente, e sem ele o
 * supervisor não tem como pôr um pedido na mão do operador. O painel monta o
 * conteúdo e desenha o código LOCALMENTE — nada sai do navegador.
 *
 * A regra que justifica o arquivo: **o que este gerador aceita, o parser do
 * coletor precisa aceitar**. Um QR impresso que o app recusa custa uma viagem
 * ao corredor, com o papel na mão e o pedido parado. Por isso cada validação
 * aqui é o espelho de uma recusa de `mobile/src/coleta/qrDeColeta.ts` — e a
 * mais sutil delas, a ambiguidade da vírgula decimal, é justamente a que já
 * derrubou uma leitura em produção de teste: `…,350,20` tanto pode ser
 * "entrega 350, quantidade 20" quanto "quantidade 350,20", e o parser recusa
 * as duas leituras em vez de adivinhar.
 *
 * Enquanto o `@wms/shared` não existe, estas regras estão escritas duas vezes
 * (aqui e no app). É a dívida registrada no PROMPT.md; o dia em que o pacote
 * existir, este módulo consome o mesmo código que o coletor.
 */

/** As três origens do Anexo B.1. */
export const ORIGENS = ['PV', 'RQ', 'OP'] as const;

export type OrigemDoPedido = (typeof ORIGENS)[number];

/**
 * Subdelimitador da referência composta de OP: ordem, etapa e sequência.
 *
 * Barra vertical, e não hífen: `PED-2024-8871` provaria que o hífen aparece
 * dentro de número de documento real, e um separador ambíguo transformaria um
 * pedido comum em chave de três partes.
 */
export const SUBDELIMITADOR_DA_REFERENCIA_DE_OP = '|';

/** Quantas partes a referência de OP tem. */
const PARTES_DA_REFERENCIA_DE_OP = 3;

/** O mesmo teto de `codigoSchema` no middleware. */
const TAMANHO_MAXIMO_DO_CODIGO = 60;

/** `codDep` do Senior é `String(010)` (Anexo B.1, campos 5-6). */
const TAMANHO_MAXIMO_DO_DEPOSITO = 10;

/** O mesmo teto de `quantidadeSchema`: `Decimal(18,6)`. */
const DIGITOS_INTEIROS_DA_QUANTIDADE = 12;
const CASAS_DECIMAIS_DA_QUANTIDADE = 6;

const SEPARADOR_DE_CAMPO = ',';
const FIM_DE_LINHA = ';';

const TEXTO_DECIMAL = /^-?\d+(\.\d+)?$/;
const INTEIRO_PURO = /^-?\d+$/;
const FRACAO_PURA = /^\d+$/;

export interface LinhaDoPedido {
  readonly sku: string;
  readonly derivacao: string;
  /**
   * Campos 5-6 do QR: `codDep` do Senior. A filial NÃO viaja no QR — é a
   * filial de trabalho do operador no app, e o coletor recusa a linha cujo
   * depósito não está na lista daquela filial.
   */
  readonly depositoColeta: string;
  readonly depositoEntrega: string;
  /** Texto decimal com PONTO, positivo. */
  readonly quantidade: string;
}

export interface PedidoParaQr {
  readonly origem: OrigemDoPedido;
  readonly referencia: string;
  readonly linhas: readonly LinhaDoPedido[];
}

export interface ProblemaDoPedido {
  /** Número da linha do documento (1 em diante); ausente = problema do cabeçalho. */
  readonly linha?: number;
  readonly campo: string;
  readonly mensagem: string;
}

export type ResultadoDoQr =
  | { readonly tipo: 'Pronto'; readonly conteudo: string }
  | { readonly tipo: 'Recusado'; readonly problemas: readonly ProblemaDoPedido[] };

function aparar(valor: string): string {
  return valor.trim();
}

/**
 * Forma canônica do depósito: aparado e em caixa alta, como o coletor compara
 * e o middleware grava. O papel sai já na forma que casa com a lista.
 */
function depositoCanonico(valor: string): string {
  return aparar(valor).normalize('NFKC').toUpperCase();
}

function conferirDeposito(
  valor: string,
  campo: string,
  rotulo: string,
  linha: number,
): ProblemaDoPedido | null {
  const texto = conferirTexto(valor, campo, rotulo, linha, false);

  if (texto !== null) {
    return texto;
  }

  if (depositoCanonico(valor).length > TAMANHO_MAXIMO_DO_DEPOSITO) {
    return {
      linha,
      campo,
      mensagem: `${rotulo} passa de ${String(TAMANHO_MAXIMO_DO_DEPOSITO)} caracteres — informe o código do depósito no Senior, não o nome.`,
    };
  }

  return null;
}

/**
 * Recusas que valem para qualquer campo textual do formato.
 *
 * O separador dentro do valor é recusa e não escape de propósito: o formato do
 * QR não prevê escape, então um valor com vírgula quebraria a linha em oito
 * campos e o coletor a rejeitaria inteira — melhor barrar aqui, com o campo à
 * vista de quem digitou.
 */
function conferirTexto(
  valor: string,
  campo: string,
  rotulo: string,
  linha: number | undefined,
  obrigatorio: boolean,
): ProblemaDoPedido | null {
  const limpo = aparar(valor);

  if (obrigatorio && limpo === '') {
    return {
      ...(linha === undefined ? {} : { linha }),
      campo,
      mensagem: `${rotulo} é obrigatório.`,
    };
  }

  if (limpo.includes(SEPARADOR_DE_CAMPO)) {
    return {
      ...(linha === undefined ? {} : { linha }),
      campo,
      mensagem: `${rotulo} não pode conter vírgula — ela separa os campos do QR.`,
    };
  }

  if (limpo.includes(FIM_DE_LINHA)) {
    return {
      ...(linha === undefined ? {} : { linha }),
      campo,
      mensagem: `${rotulo} não pode conter ponto e vírgula — ele separa as linhas do QR.`,
    };
  }

  if (limpo.length > TAMANHO_MAXIMO_DO_CODIGO) {
    return {
      ...(linha === undefined ? {} : { linha }),
      campo,
      mensagem: `${rotulo} passa de ${String(TAMANHO_MAXIMO_DO_CODIGO)} caracteres, o limite do middleware.`,
    };
  }

  return null;
}

function conferirQuantidade(valor: string, linha: number): ProblemaDoPedido | null {
  const limpo = aparar(valor);
  const base = { linha, campo: 'quantidade' };

  if (limpo.includes(SEPARADOR_DE_CAMPO)) {
    return {
      ...base,
      mensagem: 'Quantidade usa ponto como separador decimal, não vírgula (350.20).',
    };
  }

  if (!TEXTO_DECIMAL.test(limpo)) {
    return { ...base, mensagem: `Quantidade não é um número: ${limpo}.` };
  }

  if (Number(limpo) <= 0) {
    return { ...base, mensagem: 'Quantidade precisa ser maior que zero.' };
  }

  const [inteiros = '', decimais = ''] = limpo.replace('-', '').split('.');

  if (inteiros.length > DIGITOS_INTEIROS_DA_QUANTIDADE) {
    return {
      ...base,
      mensagem: `Quantidade passa de ${String(DIGITOS_INTEIROS_DA_QUANTIDADE)} dígitos inteiros, o limite do middleware.`,
    };
  }

  if (decimais.length > CASAS_DECIMAIS_DA_QUANTIDADE) {
    return {
      ...base,
      mensagem: `Quantidade passa de ${String(CASAS_DECIMAIS_DA_QUANTIDADE)} casas decimais, o limite do middleware.`,
    };
  }

  return null;
}

/**
 * A recusa que só existe porque o formato tem campo opcional.
 *
 * Depósito de entrega puramente numérico (comum no Senior: `02`) seguido de
 * quantidade inteira produz `…,02,5` — que o coletor lê como duas leituras
 * possíveis e rejeita a linha inteira. Barramos aqui, dizendo o que fazer, em
 * vez de imprimir um papel que não passa. O código do depósito não se troca (é
 * o do ERP); a saída é escrever a quantidade com ponto decimal, que o coletor
 * aceita sem ambiguidade.
 */
function conferirAmbiguidade(linha: LinhaDoPedido, numero: number): ProblemaDoPedido | null {
  const entrega = depositoCanonico(linha.depositoEntrega);
  const quantidade = aparar(linha.quantidade);

  if (!INTEIRO_PURO.test(entrega) || !FRACAO_PURA.test(quantidade)) {
    return null;
  }

  return {
    linha: numero,
    campo: 'quantidade',
    mensagem:
      `Depósito de entrega "${entrega}" e quantidade "${quantidade}" terminam a linha com dois números seguidos, ` +
      'e o coletor recusa a leitura por ambiguidade (poderia ser a quantidade ' +
      `"${entrega},${quantidade}"). Escreva a quantidade com ponto decimal, como ${quantidade}.0.`,
  };
}

function conferirReferencia(pedido: PedidoParaQr): readonly ProblemaDoPedido[] {
  const texto = conferirTexto(pedido.referencia, 'referencia', 'Referência', undefined, true);

  if (texto !== null) {
    return [texto];
  }

  if (pedido.origem !== 'OP') {
    return [];
  }

  const partes = aparar(pedido.referencia).split(SUBDELIMITADOR_DA_REFERENCIA_DE_OP);
  const completa =
    partes.length === PARTES_DA_REFERENCIA_DE_OP && partes.every((parte) => parte.trim() !== '');

  return completa
    ? []
    : [
        {
          campo: 'referencia',
          mensagem:
            'Componente de OP precisa de ordem, etapa e sequência, separadas por ' +
            `"${SUBDELIMITADOR_DA_REFERENCIA_DE_OP}" (ex.: OP-99${SUBDELIMITADOR_DA_REFERENCIA_DE_OP}10${SUBDELIMITADOR_DA_REFERENCIA_DE_OP}3). ` +
            'Sem as três, o middleware não sabe a qual componente devolver a separação.',
        },
      ];
}

function conferirLinha(linha: LinhaDoPedido, numero: number): readonly ProblemaDoPedido[] {
  const problemas = [
    conferirTexto(linha.sku, 'sku', 'Produto', numero, true),
    conferirTexto(linha.derivacao, 'derivacao', 'Derivação', numero, true),
    conferirDeposito(linha.depositoColeta, 'depositoColeta', 'Depósito de coleta', numero),
    conferirDeposito(linha.depositoEntrega, 'depositoEntrega', 'Depósito de entrega', numero),
    conferirQuantidade(linha.quantidade, numero),
    conferirAmbiguidade(linha, numero),
  ];

  return problemas.filter((problema): problema is ProblemaDoPedido => problema !== null);
}

function montarLinha(pedido: PedidoParaQr, linha: LinhaDoPedido): string {
  const campos = [
    pedido.origem,
    aparar(pedido.referencia),
    aparar(linha.sku),
    aparar(linha.derivacao),
    depositoCanonico(linha.depositoColeta),
    depositoCanonico(linha.depositoEntrega),
    aparar(linha.quantidade),
  ];

  return `${campos.join(SEPARADOR_DE_CAMPO)}${FIM_DE_LINHA}`;
}

/**
 * Monta o conteúdo do QR, ou devolve TODOS os problemas de uma vez.
 *
 * Todos de uma vez, e não o primeiro: quem está montando um pedido de dez
 * linhas não deve descobrir os erros um a um, imprimindo dez vezes.
 */
export function montarQrDePedido(pedido: PedidoParaQr): ResultadoDoQr {
  const problemas = [
    ...conferirReferencia(pedido),
    ...pedido.linhas.flatMap((linha, indice) => conferirLinha(linha, indice + 1)),
  ];

  if (pedido.linhas.length === 0) {
    problemas.push({
      campo: 'linhas',
      mensagem: 'O pedido precisa de pelo menos uma linha para virar QR.',
    });
  }

  if (problemas.length > 0) {
    return { tipo: 'Recusado', problemas };
  }

  return {
    tipo: 'Pronto',
    conteudo: pedido.linhas.map((linha) => montarLinha(pedido, linha)).join('\n'),
  };
}
